import { Link } from "react-router-dom";
import { MapPin, Tag, Clock } from "lucide-react";
import FavoriteButton from "@/components/FavoriteButton";
import ShareButton from "@/components/ShareButton";
import DemoRibbon from "@/components/DemoRibbon";

export default function OfferCard({ offer, showFav = true, onUnfav }) {
  const endDate = offer.end_date ? new Date(offer.end_date) : null;
  // Jours restants en comparant les jours calendaires (l'offre reste valable jusqu'à la fin de son dernier jour).
  let daysLeft = null;
  if (endDate && !isNaN(endDate)) {
    const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    daysLeft = Math.round((endDay - today) / 86400000);
  }
  const isNew = offer.created_at && (Date.now() - new Date(offer.created_at).getTime()) < 7 * 86400 * 1000;

  return (
    <Link to={`/offre/${offer.id}`} data-testid={`offer-card-${offer.id}`}
          className="card-hover group bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col">
      <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
        {offer.image ? (
          <img src={(offer.images && offer.images[0]) || offer.image} alt={offer.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-300"><Tag size={40} /></div>
        )}
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap max-w-[70%]">
          <span className="px-2.5 py-1 rounded-full text-xs font-extrabold tracking-wide bg-rose-500 text-white shadow">{offer.discount}</span>
          {offer.coverage === "department" && (
            <span className="px-2 py-1 rounded-full text-xs font-bold bg-slate-900 text-amber-300 border border-amber-400/50">Tout le 28</span>
          )}
          {isNew && <span className="px-2 py-1 rounded-full text-xs font-bold bg-white/95 text-emerald-700 border border-emerald-200">Nouveau</span>}
        </div>
        {showFav && (
          <div className="absolute top-3 right-3 flex flex-col gap-1.5">
            <FavoriteButton type="offer" id={offer.id} onToggle={(v) => { if (!v) onUnfav?.(); }}/>
            <ShareButton
              variant="icon"
              url={`${process.env.REACT_APP_BACKEND_URL}/api/offre-vitrine/${offer.id}`}
              title={offer.title}
              text={`${offer.discount ? offer.discount + " · " : ""}${offer.title}${offer.business?.name ? " — " + offer.business.name : ""}`}
              testId={`offer-share-${offer.id}`}
              onShare={(ch) => { try { fetch(`${process.env.REACT_APP_BACKEND_URL}/api/offers/${offer.id}/share?channel=${ch}`, { method: "POST", keepalive: true }); } catch (e) {} }}
            />
          </div>
        )}
        {offer.demo && <DemoRibbon />}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-1">
          {offer.category?.name || "Bon plan"}
        </div>
        <h3 className="font-heading font-semibold text-slate-900 leading-snug line-clamp-2">{offer.title}</h3>
        <p className="mt-1 text-sm text-slate-500 line-clamp-2">{offer.description}</p>
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
          <span className="text-slate-600 truncate">{offer.business?.name}</span>
          <span className="inline-flex items-center gap-1 text-slate-500">
            <MapPin size={12}/> {offer.coverage === "department" ? "Tout l'Eure-et-Loir" : offer.city}
          </span>
        </div>
        {offer.distance_km != null && (
          <div className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold" data-testid={`offer-distance-${offer.id}`}>
            <MapPin size={12}/> à {offer.distance_km} km de vous
          </div>
        )}
        {daysLeft !== null && daysLeft >= 0 && (
          <div className="mt-2 inline-flex items-center gap-1 text-xs text-amber-700" data-testid={`offer-countdown-${offer.id}`}>
            <Clock size={12}/> {daysLeft > 0 ? `Expire dans ${daysLeft}j` : "Expire aujourd'hui"}
          </div>
        )}
        {daysLeft !== null && daysLeft < 0 && (
          <div className="mt-2 inline-flex items-center gap-1 text-xs text-slate-400" data-testid={`offer-countdown-${offer.id}`}>
            <Clock size={12}/> Offre terminée
          </div>
        )}
      </div>
    </Link>
  );
}
